// Voice (text-to-speech) chooser, shown in the Avatar lab. Lets the owner pick
// which system voice the twin speaks with, plus rate and pitch, and preview it.
// OpenVoice is offered as a future engine (a local bridge would be required);
// until that exists, synthesis falls back to the system voice.

import { useEffect, useState } from 'react';
import { usePrefs } from '../lib/prefs.js';
import { useSpeaker } from '../lib/speech.js';
import { useTtsSettings, useVoices, fetchTtsStatus, effectiveEngine, type TtsServerStatus } from '../lib/tts.js';
import { useTwinName } from '../lib/twin.js';

const SAMPLE: Record<string, string> = {
  fr: 'Bonjour, c’est moi. Voici comment je sonne quand je te parle.',
  en: 'Hello, it’s me. This is how I sound when I talk to you.',
  es: 'Hola, soy yo. Así es como sueno cuando te hablo.',
};

export function VoiceControls() {
  const [prefs] = usePrefs();
  const [tts, update] = useTtsSettings();
  const voices = useVoices();
  const speaker = useSpeaker();
  const twinName = useTwinName();
  const [allLangs, setAllLangs] = useState(false);
  const [elStatus, setElStatus] = useState<TtsServerStatus | null>(null);

  useEffect(() => { fetchTtsStatus().then(setElStatus).catch(() => {}); }, []);

  const lang = prefs.language;
  const shown = allLangs ? voices : voices.filter((v) => v.lang.toLowerCase().startsWith(lang));

  function test() {
    speaker.stop();
    speaker.speak(SAMPLE[lang] ?? SAMPLE.en!, lang);
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="text-[11px] text-ink/55">Voice — text to speech</div>

      {/* Engine */}
      <div className="flex items-center gap-1.5 flex-wrap">
        <EngineBtn on={effectiveEngine(tts) === 'system'} onClick={() => update({ engine: 'system', engineExplicit: true })}>System</EngineBtn>
        <EngineBtn on={effectiveEngine(tts) === 'elevenlabs'} onClick={() => update({ engine: 'elevenlabs', engineExplicit: true })}>
          ElevenLabs{elStatus ? (elStatus.configured ? ' ✓' : ' ·') : ''}
        </EngineBtn>
        <EngineBtn on={effectiveEngine(tts) === 'openvoice'} onClick={() => update({ engine: 'openvoice', engineExplicit: true })}>OpenVoice</EngineBtn>
        {speaker.speaking && (
          <button onClick={() => speaker.stop()}
            className="ml-1 px-2.5 py-1 rounded-full border border-rose-300/40 text-rose-300/80 text-[11px] hover:text-rose-300">stop</button>
        )}
      </div>

      {tts.engine === 'elevenlabs' && (
        elStatus?.configured ? (
          <p className="font-serif italic text-[12px] text-emerald-300/80 leading-snug">
            Voix cloud {twinName} active{elStatus.voiceId ? ` (voice ${elStatus.voiceId})` : ''}. Les clips sont
            mis en cache — une phrase déjà dite ne re-coûte rien. Si l’API échoue, retour à la voix système.
          </p>
        ) : (
          <p className="font-serif italic text-[12px] text-amber/80 leading-snug">
            ElevenLabs n’est pas configuré. Mets ta clé API et le <b>voice id</b> dans
            <span className="font-mono not-italic text-ink/60"> Réglages → Servers</span> (clone ta voix sur
            elevenlabs.io). En attendant, la voix système est utilisée.
          </p>
        )
      )}

      {tts.engine === 'openvoice' && (
        <p className="font-serif italic text-[12px] text-amber/80 leading-snug">
          OpenVoice est un service local (named-pipe, desktop) — pas de GPU sur le VPS. Un pont serait requis ;
          voir <span className="font-mono not-italic text-ink/60">docs/TTS_OPENVOICE.md</span>. En attendant, la
          synthèse utilise la voix système ci-dessous.
        </p>
      )}

      {/* System voice picker */}
      {!speaker.supported ? (
        <p className="font-serif italic text-[12px] text-ink/45">
          La synthèse vocale n’est pas disponible dans ce navigateur.
        </p>
      ) : (
        <>
          <label className="flex flex-col gap-1">
            <span className="text-[11px] text-ink/45">
              Voice {shown.length > 0 ? `(${shown.length})` : ''}
            </span>
            <select
              value={tts.voiceURI ?? ''}
              onChange={(e) => {
                const v = voices.find((x) => x.voiceURI === e.target.value);
                update({ voiceURI: v ? v.voiceURI : null, voiceName: v ? v.name : null });
              }}
              className="px-3 py-2 rounded-lg border border-ink/15 bg-white/[0.02] font-serif text-[14px] text-ink focus:border-ink/40 focus:outline-none"
            >
              <option value="">Auto ({lang}) — default voice</option>
              {shown.map((v) => (
                <option key={v.voiceURI} value={v.voiceURI}>{v.name} — {v.lang}{v.default ? ' ★' : ''}</option>
              ))}
            </select>
          </label>

          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={allLangs} onChange={(e) => setAllLangs(e.target.checked)} className="accent-amber" />
            <span className="text-[11.5px] text-ink/50">show all languages</span>
          </label>

          {/* Rate + pitch */}
          <Range label="Rate" value={tts.rate} min={0.5} max={1.5} step={0.05} onChange={(v) => update({ rate: v })} fmt={(v) => `${v.toFixed(2)}×`} />
          <Range label="Pitch" value={tts.pitch} min={0} max={2} step={0.05} onChange={(v) => update({ pitch: v })} fmt={(v) => v.toFixed(2)} />

          <div className="flex items-center gap-2 pt-1">
            <button onClick={test}
              className="px-4 py-2 rounded-full bg-amber text-void text-[11.5px] hover:bg-particle">
              ▶ Test {twinName}’s voice
            </button>
            <button onClick={() => update({ voiceURI: null, voiceName: null, rate: 1, pitch: 1 })}
              className="px-3 py-2 rounded-full border border-ink/20 text-[11.5px] text-ink/55 hover:text-ink">
              reset
            </button>
          </div>
          <p className="font-mono text-[9px] text-ink/35">
            Voices are device-specific — this preference is saved on each device. The choice applies to the
            Converse voice loop and the Today / interview replies.
          </p>
        </>
      )}
    </div>
  );
}

function EngineBtn({ on, onClick, children }: { on: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button onClick={onClick}
      className={`px-3 py-1.5 rounded-full text-[11.5px] border transition-colors ${ on ? 'bg-amber text-void border-amber' : 'border-ink/15 text-ink/55 hover:text-ink' }`}>
      {children}
    </button>
  );
}

function Range({ label, value, min, max, step, onChange, fmt }: {
  label: string; value: number; min: number; max: number; step: number;
  onChange: (v: number) => void; fmt: (v: number) => string;
}) {
  return (
    <label className="flex flex-col gap-1">
      <div className="flex justify-between items-baseline">
        <span className="text-[11px] text-ink/45">{label}</span>
        <span className="font-mono text-[10px] text-ink/55">{fmt(value)}</span>
      </div>
      <input type="range" min={min} max={max} step={step} value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full accent-amber" />
    </label>
  );
}
